import { Component, ComponentInterface, Element, Event, EventEmitter, h, Host, Method, Prop, State, Watch } from "@stencil/core";
import dayjs, { Dayjs } from "dayjs";
import { getUniqueId } from "../../utils/unique-id";
import { initI18nDate } from "../../utils/initI18nDate";
import { DATE_ISO } from "./constants";
import {
    CalendarSheet,
    getCalendarSheet,
    getInvalidErrorMsg,
    isDateAfter,
    isDateBefore,
    restrictDateToRange
} from "./calendar-utils";
import CalendarDay from "./calendar-day";
import { Label } from "../label/label";

const baseClass = "ux-date-picker";

@Component({
    tag: "ux-date-picker",
    styleUrl: "date-picker.scss",
    shadow: false
})
export class DatePicker implements ComponentInterface {
    /** The component DOM node */
    @Element() domNode!: HTMLUxDatePickerElement;

    /** The currently active month (0-11) */
    @Prop({ mutable: true }) activeMonth?: number;
    /** The currently active year (YYYY) */
    @Prop({ mutable: true }) activeYear?: number;
    /** Format of the date value */
    @Prop() readonly dateFormat: string;
    /** Flag that controls if the field is disabled */
    @Prop() readonly disabled: boolean = false;
    /** Indicates if the field is mandatory */
    @Prop() readonly required: boolean = false;
    /** Indicates an invalid date */
    @Prop({ reflect: true, mutable: true }) error = false;
    /** Error message that is rendered in invalid state */
    @Prop({ mutable: true }) errorMsg: string | null = null;
    /** The weekday the calendar starts with (0=sunday, 1=monday, 6=saturday) */
    @Prop() readonly firstWeekday: number = 1;
    /** Initial date, formatted in YYYY-MM-DD */
    @Prop({ mutable: true, reflect: true }) value = "";
    /** @deprecated Initial date, formatted according dateFormat pattern */
    @Prop() readonly initialValue: string = this.value;
    /** Input id attribute */
    @Prop({ mutable: true }) inputId?: string;
    /** Controls the appearance of the control */
    @Prop() readonly kind: "default" | "form" = "default";
    /** Label text describing the input field */
    @Prop() readonly label?: string;
    /** Only to be used in the standard format, will render the label at the top */
    @Prop() readonly topLabel?: string;
    /** The language in which texts are displayed */
    @Prop() readonly language?: string;
    /** Optionally, the date can be restricted to a maximum value (YYYY-MM-DD) */
    @Prop() readonly maxDate?: string;
    /** Optionally, the date can be restricted to a minimum value (YYYY-MM-DD) */
    @Prop() readonly minDate?: string;
    /** Input name attribute */
    @Prop() readonly name: string = "date";
    /** Input placeholder text */
    @Prop() readonly placeholder?: string;
    /** Optional range start date (YYYY-MM-DD) */
    @Prop() readonly rangeStart: string = "";
    /** Optional range end date (YYYY-MM-DD) */
    @Prop() readonly rangeEnd: string = "";
    /** Label for the reset button */
    @Prop() readonly resetText?: string;
    /** Label for the today button */
    @Prop() readonly todayText?: string;

    /** Flag that indicates if the calendar is active */
    @State() isActive = false;
    /** Storage for the currently visible calendar days */
    @State() calendarSheet: CalendarSheet | null = null;
    /** Storage for the selected day */
    @State() selectedDay: Dayjs | null = null;
    /** Storage for the ISO date of today */
    @State() todayDateISO: string | null = null;

    /** the value that was used when the component was first initialized */
    // @ts-expect-error Check for typing
    private originalValue: string = null;

    /** Select a date */
    @Method()
    async selectDate(value: string) {
        const d = dayjs(value);
        if (d.isValid()) {
            this.selectedDay = restrictDateToRange(d, this.minDate, this.maxDate);
            this.activeYear = this.selectedDay.year().valueOf();
            this.activeMonth = this.selectedDay.month().valueOf();
        } else {
            this.selectedDay = null;
        }
        this.setValue();
    }

    private setValue() {
        this.value = this.selectedDay === null ? "" : this.selectedDay.format(DATE_ISO);
        this.setOriginalValue();
    }

    private setOriginalValue() {
        if (this.originalValue === null) {
            this.originalValue = this.value;
        }
    }

    /** Emitted when a date is selected  */
    @Event({ eventName: "uxDateSelect", bubbles: true, cancelable: true })
    dateSelected!: EventEmitter<{ name: string; value: string }>;

    /** Emitted when hovering a calendar day  */
    @Event({ eventName: "uxDateHover", bubbles: true, cancelable: false })
    dateHover!: EventEmitter<{ name: string; value: string }>;

    @Watch("inputId")
    setInputId() {
        this.inputId = this.inputId || getUniqueId();
    }

    @Watch("language")
    setLanguage() {
        if (this.language !== undefined && this.language !== "") {
            initI18nDate(this.language)
                .then(() => this.updateCalendar())
                .catch();
        }
    }

    @Watch("value")
    updateValue() {
        if (this.selectedDay !== null && this.value === this.selectedDay.format(DATE_ISO)) {
            return;
        }
        const newDate = dayjs(this.value, DATE_ISO);
        if (newDate.isValid()) {
            this.selectDate(newDate.format(DATE_ISO));
            return;
        }
        this.selectedDay = null;
    }

    @Watch("initialValue")
    setInitialValue() {
        if (this.initialValue !== "") {
            const initialDate = this.dateFormat ? dayjs(this.initialValue, this.dateFormat) : dayjs(this.initialValue);
            if (initialDate.isValid()) {
                this.selectDate(initialDate.format(DATE_ISO));
                return;
            }
        }
        this.selectedDay = null;
    }

    @Watch("activeMonth")
    @Watch("activeYear")
    @Watch("firstWeekday")
    @Watch("minDate")
    @Watch("maxDate")
    setCalendarOptions() {
        this.updateCalendar();
    }

    @Watch("isActive")
    setIsActive() {
        if (this.isActive) {
            if (this.selectedDay !== null) {
                this.activeYear = this.selectedDay.year().valueOf();
                this.activeMonth = this.selectedDay.month().valueOf();
            }
            document.addEventListener("click", this.handleClickDoc);
        } else {
            document.removeEventListener("click", this.handleClickDoc);
            if (this.required && this.selectedDay === null) {
                this.error = true;
                this.errorMsg = getInvalidErrorMsg(this.domNode) || this.errorMsg;
            } else if (this.selectedDay !== null) {
                this.updateInputValue();
            }
        }
    }

    /** DOM node of the input element */
    private inputElement: HTMLInputElement | null = null;

    private updateInputValue() {
        const { inputElement, selectedDay, dateFormat } = this;
        if (inputElement === null) {
            return;
        }
        if (selectedDay !== null) {
            inputElement.value = selectedDay.format(dateFormat);
            this.error = false;
        } else {
            inputElement.value = "";
        }
    }

    private updateCalendar() {
        const { activeYear, activeMonth, firstWeekday, language } = this;
        if (typeof activeYear !== "number" || typeof activeMonth !== "number") {
            this.calendarSheet = null;
            return;
        }
        this.calendarSheet = getCalendarSheet(activeYear, activeMonth, firstWeekday, language);
    }

    private reset() {
        this.selectDate(this.originalValue);
        this.dateSelected.emit({
            name: this.name,
            value: this.value
        });
        this.isActive = false;
    }

    private showToday() {
        const date = dayjs(this.todayDateISO);
        if (!date.isValid()) {
            return;
        }
        this.activeYear = date.year();
        this.activeMonth = date.month();
    }

    private getDayIsDisabled(dateISO: string): boolean {
        // @ts-expect-error Check for typing
        return isDateAfter(dateISO, this.maxDate) || isDateBefore(dateISO, this.minDate);
    }

    private getIsInRange(dateISO: string): boolean {
        const { rangeStart, rangeEnd } = this;
        if (this.rangeStart === "" && this.rangeEnd === "") {
            return false;
        }
        if (rangeStart !== "" && rangeEnd === "") {
            return isDateAfter(dateISO, rangeStart, true);
        }
        if (rangeEnd !== "" && rangeStart === "") {
            return isDateBefore(dateISO, rangeEnd, true);
        }
        return isDateAfter(dateISO, rangeStart, true) && isDateBefore(dateISO, rangeEnd, true);
    }

    private stepActiveMonth = (step: number) => {
        // @ts-expect-error Check for typing
        let nextMonth: number = (this.activeMonth += step);
        if (nextMonth > 11) {
            // @ts-expect-error Check for typing
            this.activeYear++;
            nextMonth -= 12;
        } else if (nextMonth < 0) {
            // @ts-expect-error Check for typing
            this.activeYear--;
            nextMonth += 12;
        }
        this.activeMonth = nextMonth;
    };

    private stepActiveYear = (step: number) => {
        // @ts-expect-error Check for typing
        this.activeYear += step;
    };

    private handleClickField = () => {
        if (this.disabled) {
            return;
        }
        this.isActive = !this.isActive;
    };

    private handleSelectDay = (day: string) => {
        const selectedDay = restrictDateToRange(day, this.minDate, this.maxDate);
        const event = this.dateSelected.emit({
            name: this.name,
            value: selectedDay.format(DATE_ISO)
        });
        if (!event.defaultPrevented) {
            this.selectedDay = selectedDay;
            this.error = false;
        }
        this.isActive = false;
        this.setValue();
    };

    private handleHoverDay = (day: Dayjs) => {
        this.dateHover.emit({
            name: this.name,
            value: day.format(DATE_ISO)
        });
    };

    private handleClickDoc = (event: MouseEvent) => {
        if (!this.isActive) {
            return;
        }
        if ((event.target as HTMLElement).closest(baseClass) === this.domNode) {
            return;
        }
        this.isActive = false;
    };

    private handleInputChange = (event: InputEvent) => {
        const value = (event.target as HTMLInputElement).value;
        const d = dayjs(value, this.dateFormat);
        if (d.isValid()) {
            this.selectDate(d.format(DATE_ISO));
            this.error = false;
        } else if (value.length > 0 || this.required) {
            this.error = true;
            this.errorMsg = getInvalidErrorMsg(this.domNode) || this.errorMsg;
        }
    };

    componentWillLoad() {
        const today = dayjs();
        this.todayDateISO = today.format(DATE_ISO);
        if (typeof this.activeYear !== "number") {
            this.activeYear = today.year();
        }
        if (typeof this.activeMonth !== "number") {
            this.activeMonth = today.month();
        }
        this.setInputId();
        this.setLanguage();
        this.setInitialValue();
        this.updateValue();
        this.setCalendarOptions();
    }

    disconnectedCallback() {
        document.removeEventListener("click", this.handleClickDoc);
    }

    render() {
        const {
            activeMonth,
            activeYear,
            calendarSheet,
            dateFormat,
            disabled,
            error,
            errorMsg,
            inputId,
            isActive,
            kind,
            label,
            topLabel,
            name,
            placeholder,
            required,
            selectedDay,
            todayDateISO,
            todayText,
            resetText
        } = this;

        const selectedDateISO = selectedDay !== null ? selectedDay.format(DATE_ISO) : "";
        const selectedDateFormatted = selectedDay !== null ? selectedDay.format(dateFormat) : "";
        const hasFooter = todayText !== undefined || resetText !== undefined;
        const hasTopLabel = topLabel && topLabel.length > 0;

        return (
            <Host
                class={{
                    [baseClass]: true,
                    [`${baseClass}--disabled`]: !!disabled,
                    [`${baseClass}--invalid`]: !!error,
                    [`${baseClass}--kind-default`]: kind === "default",
                    [`${baseClass}--kind-form`]: kind === "form"
                }}
            >
                {hasTopLabel && (
                    <Label
                        required={required}
                        label={topLabel}
                        error={error}
                        // @ts-expect-error Check for typing
                        errorMsg={errorMsg}
                        htmlFor={inputId}
                        labelClasses={`${baseClass}__label--top`}
                    />
                )}
                <div
                    class={{
                        [`${baseClass}__field-wrapper`]: true,
                        "ux-u-shadow": kind === "default"
                    }}
                >
                    <Label
                        // @ts-expect-error Check for typing
                        required={hasTopLabel ? null : required}
                        label={label}
                        // @ts-expect-error Check for typing
                        error={hasTopLabel ? null : error}
                        // @ts-expect-error Check for typing
                        errorMsg={hasTopLabel ? null : errorMsg}
                        htmlFor={inputId}
                    />
                    <div class={`${baseClass}__field`} onClick={this.handleClickField}>
                        <input
                            class={{
                                "ux-input": true,
                                "ux-input--error": !!error
                            }}
                            disabled={disabled}
                            id={inputId}
                            placeholder={placeholder}
                            // @ts-expect-error Check for typing
                            ref={(el) => (this.inputElement = el)}
                            type="text"
                            aria-invalid={error ? "true" : "false"}
                            onInput={this.handleInputChange}
                            // @ts-expect-error Check for typing
                            onBlur={this.handleInputChange}
                            value={selectedDateFormatted}
                            required={required}
                            autocomplete={"off"}
                        />
                        <input type="hidden" name={name} value={this.value} />
                        <ux-icon name="calendar" weight="light"></ux-icon>
                    </div>
                </div>
                {isActive && (
                    <div class={`${baseClass}__container`}>
                        <div class={`${baseClass}__header`}>
                            <button type="button" onClick={() => this.stepActiveYear(-1)}>
                                <ux-icon name="chevron-left-double" weight="bold"></ux-icon>
                            </button>
                            <button type="button" onClick={() => this.stepActiveMonth(-1)}>
                                <ux-icon name="chevron-left" weight="bold"></ux-icon>
                            </button>
                            <span class={`${baseClass}__title`}>
                                {/* @ts-expect-error Check for typing */}
                                {calendarSheet.monthName} {activeYear}
                            </span>
                            <button type="button" onClick={() => this.stepActiveMonth(1)}>
                                <ux-icon name="chevron-right" weight="bold"></ux-icon>
                            </button>
                            <button type="button" onClick={() => this.stepActiveYear(1)}>
                                <ux-icon name="chevron-right-double" weight="bold"></ux-icon>
                            </button>
                        </div>
                        <div class={`${baseClass}__calendar`}>
                            {/** @ts-expect-error Check for typing */}
                            {calendarSheet.dayNames.map((dayName) => (
                                <span
                                    class={{
                                        [`${baseClass}__calendar-day`]: true,
                                        [`${baseClass}__calendar-head`]: true
                                    }}
                                >
                                    {dayName}
                                </span>
                            ))}
                            {/** @ts-expect-error Check for typing */}
                            {calendarSheet.days.map((dateISO) => {
                                const date = dayjs(dateISO);
                                return (
                                    <CalendarDay
                                        baseClass={baseClass}
                                        date={date}
                                        isDisabled={this.getDayIsDisabled(dateISO)}
                                        isInactive={date.month() !== activeMonth}
                                        isHighlighted={this.getIsInRange(dateISO)}
                                        isSelected={dateISO === selectedDateISO}
                                        isToday={dateISO === todayDateISO}
                                        // @ts-expect-error Check for typing
                                        onSelectDay={this.handleSelectDay}
                                        onHoverDay={this.handleHoverDay}
                                    />
                                );
                            })}
                        </div>
                        {hasFooter && (
                            <div class={`${baseClass}__footer`}>
                                {resetText && (
                                    <button class="ux-text-link ux-text-link--green" onClick={() => this.reset()} type="button">
                                        {resetText}
                                    </button>
                                )}
                                {todayText && (
                                    <button class="ux-text-link ux-text-link--green" onClick={() => this.showToday()} type="button">
                                        {todayText}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </Host>
        );
    }
}
